import React from 'react';
import { Navigate, Route } from 'react-router-dom';
import RequireCapability, { SettingsSkeleton } from 'src/components/Base/RequireCapability';
import MainLayout from 'src/components/Layout/MainLayout';
import StorefrontLayout from 'src/components/Layout/StorefrontLayout';
import { useStorefrontNav } from 'src/hooks/useStorefrontNav';
import StorefrontAnalytics from 'src/pages/Storefront/Analytics';
import StorefrontAppearance from 'src/pages/Storefront/Appearance';
import StorefrontContent from 'src/pages/Storefront/Content';
import StorefrontExtras from 'src/pages/Storefront/Extras';
import StorefrontMessages from 'src/pages/Storefront/Messages';
import StorefrontNews from 'src/pages/Storefront/News';
import StorefrontOrdering from 'src/pages/Storefront/Ordering';
import StorefrontSections from 'src/pages/Storefront/Sections';
import StorefrontSeo from 'src/pages/Storefront/Seo';
import StorefrontSite from 'src/pages/Storefront/Site';

// Extracted from App.tsx for the same reason settingsRoutes.tsx is: a unit test can then import
// just this tree without pulling in every other route group and their transitive dependencies.
function StorefrontIndexRedirect() {
  const { ready, firstAllowedPath } = useStorefrontNav();
  if (!ready) {
    return <SettingsSkeleton />;
  }
  return <Navigate to={firstAllowedPath} replace />;
}

/**
 * Every leaf passes `fallbackPath="/dashboard/selling"` explicitly. The guard's default is the
 * settings area's first allowed path; a member who reaches /storefront by URL must not be
 * dropped into a settings screen they also cannot use.
 */
const GUARD_FALLBACK = '/dashboard/selling';

export const storefrontLeafRoutes = (
  <Route element={<MainLayout />}>
    {/* The guard sits here, on the `/storefront` route itself, and not only on each leaf below
        it: `/storefront` is the Site screen — an addressable page the nav config links to —
        unlike `/settings`, which is a bare shell whose index redirects to a capability-filtered
        child. So the area's entry point needs the same guard as its leaves, and guarding it here
        is also what makes `/storefront` a guarded leaf to `collectPaths`/`collectCapabilities` in
        `src/config/storefrontRoutes.test.ts`. */}
    <Route
      path="storefront"
      element={
        <RequireCapability capability="canManageRestaurantSettings" fallbackPath={GUARD_FALLBACK}>
          <StorefrontLayout />
        </RequireCapability>
      }
    >
      <Route index element={<StorefrontSite />} />
      <Route path="*" element={<StorefrontIndexRedirect />} />
      <Route
        path="appearance"
        element={
          <RequireCapability capability="canManageRestaurantSettings" fallbackPath={GUARD_FALLBACK}>
            <StorefrontAppearance />
          </RequireCapability>
        }
      />
      <Route
        path="content"
        element={
          <RequireCapability capability="canManageRestaurantSettings" fallbackPath={GUARD_FALLBACK}>
            <StorefrontContent />
          </RequireCapability>
        }
      />
      <Route
        path="sections"
        element={
          <RequireCapability capability="canManageRestaurantSettings" fallbackPath={GUARD_FALLBACK}>
            <StorefrontSections />
          </RequireCapability>
        }
      />
      <Route
        path="extras"
        element={
          <RequireCapability capability="canManageRestaurantSettings" fallbackPath={GUARD_FALLBACK}>
            <StorefrontExtras />
          </RequireCapability>
        }
      />
      <Route
        path="seo"
        element={
          <RequireCapability capability="canManageRestaurantSettings" fallbackPath={GUARD_FALLBACK}>
            <StorefrontSeo />
          </RequireCapability>
        }
      />
      <Route
        path="ordering"
        element={
          <RequireCapability capability="canManageRestaurantSettings" fallbackPath={GUARD_FALLBACK}>
            <StorefrontOrdering />
          </RequireCapability>
        }
      />
      <Route
        path="analytics"
        element={
          <RequireCapability capability="canManageRestaurantSettings" fallbackPath={GUARD_FALLBACK}>
            <StorefrontAnalytics />
          </RequireCapability>
        }
      />
      <Route
        path="news"
        element={
          <RequireCapability capability="canManageRestaurantSettings" fallbackPath={GUARD_FALLBACK}>
            <StorefrontNews />
          </RequireCapability>
        }
      />
      <Route
        path="messages"
        element={
          <RequireCapability capability="canManageRestaurantSettings" fallbackPath={GUARD_FALLBACK}>
            <StorefrontMessages />
          </RequireCapability>
        }
      />
    </Route>
  </Route>
);
